import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DefaultGameRules, Event, GameConstant, GameRuleKey, GameRules, Lobby as LobbyType, LobbyCheckStatus, LobbyPlayerState, LobbyState, RoomAccessibility, type LobbyCheckResult } from "@uno/protocol";
import { AppContext } from "context";
import ButtonHapticWrapper from "component/Button/ButtonHapticWrapper";
import Container from "component/Layout/Container";
import { AnimatePresence, motion } from "framer-motion";
import useStoreToast from "store/useStoreToast";
import useStorePlayer from "store/useStorePlayer";
import Avatar from "component/Ui/Avatar";
import StatusTitle from "./components/StatusTitle";
import PlayerSlot from "./components/PlayerSlot";
import EmptySlot from "./components/EmptySlot";
import ToggleJoinButton from "./components/ToggleJoinButton";
import ToggleStartButton from "./components/ToggleStartButton";
import InvitePanel from "./components/InvitePanel";
import InviteSheet from "./components/InviteSheet";
import RulesDrawer from "./components/RulesDrawer";
import { DESKTOP_QUERY, HouseRulesBar, HouseRulesDialog } from "./components/HouseRules";
import useMediaQuery from "hook/useMediaQuery";
import { HOUSE_RULES } from "utils/HouseRules";
import Tooltip from "component/Tooltip";
import hostIntegration from "../../integrations/host";
import "./index.scss";

const Lobby = () => {
    const { socket } = React.useContext(AppContext);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const showToast = useStoreToast((state) => state.show);
    const { lobbyId } = useParams();
    const [lobby, setLobby] = React.useState<LobbyType | null>(null);
    const [copiedLink, setCopiedLink] = React.useState(false);
    const [copiedCode, setCopiedCode] = React.useState(false);
    const [showInviteSheet, setShowInviteSheet] = React.useState(false);
    const [showRules, setShowRules] = React.useState(false);
    const [showHouseRules, setShowHouseRules] = React.useState(false);
    const [pendingRules, setPendingRules] = React.useState<GameRules | null>(null);
    const statistics = useStorePlayer((state) => state.player?.statistics);
    const isFirstGame = !statistics || statistics.games === 0;
    const [showRulesTooltip] = React.useState(true);
    const navigatingToRoom = React.useRef(false);

    const fallbackLobbyUrl = `${window.location.origin}/lobby/${lobbyId}`;
    const [lobbyUrl, setLobbyUrl] = React.useState(fallbackLobbyUrl);
    const lobbyCode = lobbyId?.substring(0, 4).toUpperCase() || "XXXX";

    const isPrivate = lobby?.accessibility === RoomAccessibility.Private;
    const currentPlayer = lobby?.players.find(p => p.socket.id === socket?.id);
    const currentSpectator = lobby?.spectators?.find(p => p.socket.id === socket?.id);
    const isSpectator = !currentPlayer && !!currentSpectator;
    const isReady = isSpectator
        ? currentSpectator?.lobbyState === LobbyPlayerState.Ready
        : currentPlayer?.lobbyState === LobbyPlayerState.Ready;
    const countdown = lobby && lobby.countdown > 0 ? lobby.countdown : null;
    const players = lobby?.players ?? [];
    const isLocked = lobby?.state === LobbyState.Launching || (lobby?.state === LobbyState.WaitingCountdown && !countdown);
    const allSpectators = lobby?.spectators ?? [];
    const spectators = [
        ...allSpectators.filter(s => s.socket.id === socket?.id),
        ...allSpectators.filter(s => s.socket.id !== socket?.id),
    ];
    const emptySlots = GameConstant.maxRoomPlayers - players.length;
    const isHost = lobby?.host === currentPlayer?.id;
    const rules = pendingRules ?? lobby?.rules ?? DefaultGameRules;
    const canEditRules = isPrivate && isHost && lobby?.state === LobbyState.WaitingPlayers;
    const rulesRef = React.useRef(rules);
    rulesRef.current = rules;

    const [houseRulesHintSeen, setHouseRulesHintSeen] = React.useState(false);
    const [houseRulesHintReady, setHouseRulesHintReady] = React.useState(false);
    const isDesktopLayout = useMediaQuery(DESKTOP_QUERY);
    const showHouseRulesHint = houseRulesHintReady && canEditRules && !houseRulesHintSeen;
    const houseRulesHintDelay = isFirstGame ? 3900 : 2400;

    React.useEffect(() => {
        setHouseRulesHintReady(false);
        setHouseRulesHintSeen(false);

        const timer = setTimeout(() => setHouseRulesHintReady(true), houseRulesHintDelay);

        return () => clearTimeout(timer);
    }, [lobbyId, houseRulesHintDelay]);

    const openHouseRules = React.useCallback(() => {
        setShowHouseRules(true);
        setHouseRulesHintSeen(true);
    }, []);

    React.useEffect(() => {
        if(pendingRules && lobby?.rules && HOUSE_RULES.every(({ key }) => lobby.rules[key] === pendingRules[key])) {
            setPendingRules(null);
        }
    }, [lobby?.rules, pendingRules]);

    React.useEffect(() => {
        let active = true;

        setLobbyUrl(fallbackLobbyUrl);

        if(!lobbyId || !isPrivate) {
            return () => {
                active = false;
            };
        }

        void hostIntegration.initialize().then(() => {
            if(active) {
                setLobbyUrl(
                    hostIntegration.createRoomInviteUrl(lobbyId)
                    ?? fallbackLobbyUrl,
                );
            }
        });

        return () => {
            active = false;
        };
    }, [lobbyId, isPrivate, fallbackLobbyUrl]);

    React.useEffect(() => {
        if(!lobby || !isPrivate) {
            return;
        }

        hostIntegration.syncRoom({
            id: lobby.id,
            joinable: !isLocked
                && players.length < GameConstant.maxPrivateRoomPlayers,
        });
    }, [lobby?.id, isPrivate, isLocked, players.length]);

    React.useEffect(() => {
        return () => {
            if(!navigatingToRoom.current) {
                hostIntegration.syncRoom(null);
            }
        };
    }, [lobbyId]);

    const copyLink = React.useCallback(() => {
        navigator.clipboard.writeText(lobbyUrl);
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
    }, [lobbyUrl]);

    const copyCode = React.useCallback(() => {
        navigator.clipboard.writeText(lobbyCode);
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 1500);
    }, [lobbyCode]);

    const handleToggleReady = React.useCallback(() => {
        if(lobbyId) {
            socket?.emit(Event.ToggleLobbyPlayerState, lobbyId, (result: { success: boolean; isSpectator?: boolean; reason?: string }) => {
                if(!result.success && result.reason) {
                    showToast(t(`lobby.${result.reason}`), "error");
                }
            });
        }
    }, [socket, lobbyId]);

    const handleRuleChange = React.useCallback((key: GameRuleKey, value: boolean) => {
        if(!lobbyId || !canEditRules) {
            return;
        }

        const next = { ...rulesRef.current, [key]: value };

        rulesRef.current = next;
        setPendingRules(next);
        socket?.emit(Event.LobbyUpdateRules, lobbyId, next, (result: { success: boolean; reason?: string }) => {
            if(!result.success) {
                setPendingRules(null);
            }
        });
    }, [socket, lobbyId, canEditRules]);

    const handleToggleStart = React.useCallback(() => {
        if(lobbyId) {
            socket?.emit(Event.ToggleHostStart, lobbyId, (result: { success: boolean; reason?: string }) => {
                if(!result.success && result.reason) {
                    showToast(t(`lobby.${result.reason}`), "error");
                }
            });
        }
    }, [socket, lobbyId, showToast, t]);

    React.useEffect(() => {
        if(!socket) {
            return;
        }

        let active = true;

        const onLobbyStateUpdate = (updatedLobby: LobbyType) => {
            if(navigatingToRoom.current) {
                return;
            }

            if(updatedLobby.state === LobbyState.Launching) {
                navigatingToRoom.current = true;
            }

            setLobby(updatedLobby);
        };

        const onPlayerJoinRoom = (room: { id: string }) => {
            navigatingToRoom.current = true;
            navigate(`/room/${room.id}`);
        };

        const onLobbyError = () => {
            showToast(t("lobby.error"), "error");
            navigate("/");
        };

        const checkLobby = () => {
            if(!lobbyId || !socket.connected) {
                return;
            }

            const connectionId = socket.id;

            socket.emit(Event.PlayerCheckLobby, lobbyId, (result: LobbyCheckResult) => {
                if(
                    !active
                    || !socket.connected
                    || socket.id !== connectionId
                ) {
                    return;
                }

                const actions: Record<string, () => void> = {
                    [LobbyCheckStatus.Started]: () => navigate(`/room/${(result as { roomId: string }).roomId}`),
                    [LobbyCheckStatus.NotFound]: () => { showToast(t("lobby.not_found"), "error"); navigate("/"); },
                };

                actions[result.status]?.();
            });
        };

        socket.on(Event.LobbyStateUpdate, onLobbyStateUpdate);
        socket.on(Event.PlayerJoinRoom, onPlayerJoinRoom);
        socket.on(Event.LobbyError, onLobbyError);
        socket.on("connect", checkLobby);

        if(socket.connected) {
            checkLobby();
        }

        return () => {
            active = false;
            socket.off(Event.LobbyStateUpdate, onLobbyStateUpdate);
            socket.off(Event.PlayerJoinRoom, onPlayerJoinRoom);
            socket.off(Event.LobbyError, onLobbyError);
            socket.off("connect", checkLobby);
        };
    }, [socket, navigate, lobbyId, showToast, t]);

    React.useEffect(() => {
        return () => {
            if(lobbyId) {
                socket?.emit(Event.PlayerLeaveLobby, lobbyId);
            }
        };
    }, [socket, lobbyId]);

    return (
        <Container id="lobby" className="viewport-compact:h-[100dvh] viewport-compact:min-h-0 viewport-compact:overflow-hidden" headerLeft={
            <>
                {!isLocked && (
                    <ButtonHapticWrapper>
                        <button onClick={() => navigate("/")} className="flex items-center hover:opacity-60 transition-opacity cursor-pointer">
                            <svg className="w-6 h-6 md:w-7 md:h-7 text-white drop-shadow-md" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                    </ButtonHapticWrapper>
                )}
                <Tooltip
                    text={isFirstGame ? t("rules.tooltip_first") : t("rules.tooltip")}
                    visible={showRulesTooltip}
                    placement="bottom"
                    duration={isFirstGame ? 3500 : 2000}
                    onClick={() => setShowRules(true)}
                >
                    <ButtonHapticWrapper>
                        <button className="flex items-center justify-center w-9 h-9 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full backdrop-blur-sm transition-colors cursor-pointer">
                            <svg className="w-4 h-4 text-white/70" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                            </svg>
                        </button>
                    </ButtonHapticWrapper>
                </Tooltip>
            </>
        }>

                {/* Main */}
                <div className="flex-1 flex items-center justify-center px-4 md:px-0 py-2 md:py-6 viewport-compact:min-h-0 viewport-compact:pt-0 viewport-compact:pb-2">
                    <div className="flex items-center gap-12 lg:gap-20 viewport-landscape:gap-[clamp(2rem,5vw,5rem)]">
                        {/* Left column */}
                        <div className="flex flex-col items-center gap-4 md:gap-8 viewport-landscape:gap-[clamp(0.625rem,3vh,2rem)]">
                            <StatusTitle countdown={countdown} lobbyState={lobby?.state} isPrivate={isPrivate} isReady={isReady && !isSpectator} />

                            <AnimatePresence mode="popLayout">
                                <div className="grid grid-cols-4 gap-x-3 gap-y-4 xs:gap-x-6 md:gap-x-12 md:gap-y-8 viewport-landscape:gap-x-[clamp(1.5rem,5vw,4rem)] viewport-landscape:gap-y-[clamp(1rem,4vh,2.5rem)] justify-items-center items-start">
                                    {players.map((player) => (
                                        <PlayerSlot
                                            key={player.id}
                                            player={player}
                                            isMe={player.socket.id === socket?.id}
                                            isHost={isPrivate && player.id === lobby?.host}
                                        />
                                    ))}
                                    {Array.from({ length: Math.max(0, emptySlots) }).map((_, i) => (
                                        <EmptySlot key={`empty-${i}`} isPaid={false} isPrivate={isPrivate} onInvite={() => setShowInviteSheet(true)} />
                                    ))}
                                </div>
                            </AnimatePresence>

                            {!isLocked && (
                                <div className="w-full flex flex-col items-center gap-2">
                                    {isPrivate && isHost ? (
                                        <ToggleStartButton disabled={players.length < GameConstant.minRoomPlayers} isCountdown={!!countdown} onToggle={handleToggleStart} />
                                    ) : (
                                        <ToggleJoinButton isReady={isReady} onReady={handleToggleReady} onCancel={handleToggleReady} />
                                    )}
                                </div>
                            )}

                            <div className="h-6 viewport-compact:h-5 viewport-landscape:mb-[clamp(0.75rem,2vh,1.25rem)]">
                            <AnimatePresence initial={false}>
                                {spectators.length > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="flex items-center justify-center gap-2"
                                        >
                                            <svg className="w-4 h-4 text-white/40" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                            <div className="flex -space-x-3">
                                                <AnimatePresence>
                                                    {spectators.slice(0, 3).map((spectator) => (
                                                        <motion.div
                                                            key={spectator.id}
                                                            initial={{ opacity: 0, scale: 0.5 }}
                                                            animate={{ opacity: 1, scale: 1 }}
                                                            exit={{ opacity: 0, scale: 0.5 }}
                                                            transition={{ duration: 0.25 }}
                                                        >
                                                            <Avatar src={spectator.avatar} className="w-6 h-6 border border-white/20" />
                                                        </motion.div>
                                                    ))}
                                                </AnimatePresence>
                                                {spectators.length > 3 && (
                                                    <div className="w-6 h-6 rounded-full bg-white/20 border border-white/30 flex items-center justify-center">
                                                        <span className="text-white/80 text-[9px] font-bold">+{spectators.length - 3}</span>
                                                    </div>
                                                )}
                                            </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                            </div>

                            {/* Mobile bottom bar: invite friends | house rules */}
                            {isPrivate && (
                                <div className="lg:hidden viewport-compact:hidden flex items-start justify-center gap-4">
                                    <div className="flex flex-col items-center gap-2">
                                        <span className="text-white/90 text-[10px] font-bold uppercase tracking-[0.15em]">{t("lobby.invite_friends")}</span>
                                        <div className="flex items-center gap-2.5">
                                            <ButtonHapticWrapper>
                                                <button onClick={() => setShowInviteSheet(true)} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center cursor-pointer border border-white/10">
                                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="5" height="5" rx="1" stroke="white" strokeOpacity="0.5" strokeWidth="2" /><rect x="3" y="16" width="5" height="5" rx="1" stroke="white" strokeOpacity="0.5" strokeWidth="2" /><rect x="16" y="3" width="5" height="5" rx="1" stroke="white" strokeOpacity="0.5" strokeWidth="2" strokeLinejoin="round" /><rect x="6" y="10" width="2" height="2" rx="0.5" fill="white" fillOpacity="0.5" /><path d="M12.5 2H13.5C13.776 2 14 2.224 14 2.5V5.5C14 5.776 13.776 6 13.5 6H12.5C12.224 6 12 6.224 12 6.5V7.5C12 7.776 11.776 8 11.5 8H10.5C10.224 8 10 7.776 10 7.5V4.5C10 4.224 10.224 4 10.5 4H11.5C11.776 4 12 3.776 12 3.5V2.5C12 2.224 12.224 2 12.5 2Z" fill="white" fillOpacity="0.5" /><rect x="8" y="12" width="4" height="2" rx="0.5" fill="white" fillOpacity="0.5" /><rect x="18" y="10" width="4" height="2" rx="0.5" fill="white" fillOpacity="0.5" /><rect x="12" y="18" width="4" height="2" rx="0.5" fill="white" fillOpacity="0.5" /><rect x="10" y="16" width="2" height="2" rx="0.5" fill="white" fillOpacity="0.5" /><path d="M12 11.5V10.5C12 10.224 12.224 10 12.5 10H15.5C15.776 10 16 10.224 16 10.5V14H17.5C17.776 14 18 14.224 18 14.5V15.5C18 15.776 17.776 16 17.5 16H16H14.5C14.224 16 14 15.776 14 15.5V12.5C14 12.224 13.776 12 13.5 12H12.5C12.224 12 12 11.776 12 11.5Z" fill="white" fillOpacity="0.5" /><path d="M16.5 20H17.5C17.776 20 18 19.776 18 19.5V18.5C18 18.224 18.224 18 18.5 18H19.5C19.776 18 20 17.776 20 17.5V14.5C20 14.224 20.224 14 20.5 14H21.5C21.776 14 22 14.224 22 14.5V16V18V19.5C22 19.776 21.776 20 21.5 20H20.5C20.224 20 20 20.224 20 20.5V21.5C20 21.776 19.776 22 19.5 22H16.5C16.224 22 16 21.776 16 21.5V20.5C16 20.224 16.224 20 16.5 20Z" fill="white" fillOpacity="0.5" /><path d="M2 13.5V10.5C2 10.224 2.224 10 2.5 10H3.5C3.776 10 4 10.224 4 10.5V11.5C4 11.776 4.224 12 4.5 12H5.5C5.776 12 6 12.224 6 12.5V13.5C6 13.776 5.776 14 5.5 14H2.5C2.224 14 2 13.776 2 13.5Z" fill="white" fillOpacity="0.5" /><path d="M13.5 22H10.5C10.224 22 10 21.776 10 21.5V20.5C10 20.224 10.224 20 10.5 20H11.5C11.776 20 12 19.776 12 19.5V18.5C12 18.224 12.224 18 12.5 18H15.5C15.776 18 16 18.224 16 18.5V19.5C16 19.776 15.776 20 15.5 20H14.5C14.224 20 14 20.224 14 20.5V21.5C14 21.776 13.776 22 13.5 22Z" fill="white" fillOpacity="0.5" /></svg>
                                                </button>
                                            </ButtonHapticWrapper>
                                            <ButtonHapticWrapper>
                                                <button onClick={copyLink} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center cursor-pointer border border-white/10">
                                                    <svg className="w-4 h-4 text-white/50" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                                                </button>
                                            </ButtonHapticWrapper>
                                            <ButtonHapticWrapper>
                                                <button onClick={() => { if(navigator.share) { navigator.share({ url: lobbyUrl }); } }} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center cursor-pointer border border-white/10">
                                                    <svg className="w-4 h-4 text-white/50" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" /></svg>
                                                </button>
                                            </ButtonHapticWrapper>
                                        </div>
                                    </div>
                                    <div className="w-px self-stretch bg-white/15"></div>
                                    <HouseRulesBar rules={rules} onOpen={openHouseRules} showHint={showHouseRulesHint && !isDesktopLayout} />
                                </div>
                            )}
                        </div>

                        {isPrivate && (
                            <InvitePanel
                                lobbyUrl={lobbyUrl}
                                lobbyCode={lobbyCode}
                                copiedLink={copiedLink}
                                copiedCode={copiedCode}
                                onCopyLink={copyLink}
                                onCopyCode={copyCode}
                            >
                                <HouseRulesBar variant="column" rules={rules} onOpen={openHouseRules} showHint={showHouseRulesHint && isDesktopLayout} />
                            </InvitePanel>
                        )}
                    </div>
                </div>
            {/* Mobile invite sheet */}
            <InviteSheet
                isOpen={showInviteSheet}
                onClose={() => setShowInviteSheet(false)}
                lobbyUrl={lobbyUrl}
                lobbyCode={lobbyCode}
                copiedLink={copiedLink}
                copiedCode={copiedCode}
                onCopyLink={copyLink}
                onCopyCode={copyCode}
            />
            {isPrivate && (
                <HouseRulesDialog
                    isOpen={showHouseRules}
                    onClose={() => setShowHouseRules(false)}
                    rules={rules}
                    editable={canEditRules}
                    onChange={handleRuleChange}
                />
            )}
            <RulesDrawer isOpen={showRules} onClose={() => setShowRules(false)} houseRules={isPrivate ? rules : undefined} />
        </Container>
    );
};

export default Lobby;
